/**
 * BoticarIA Audio — sonido generativo con WebAudio (sin archivos, sin dependencias).
 * - hum de "pensando": drone suave en quinta (108 Hz + 162 Hz) con LFO de respiración
 * - chime de "respuesta": dos notas suaves (C5 → E5) con caída natural
 * Volúmenes deliberadamente bajos: presencia, no protagonismo.
 * El AudioContext se crea recién en el primer gesto del usuario (política de autoplay).
 */

class OrbAudio {
  constructor() {
    this.ctx = null;
    this.hum = null;
    this.enabled = true;
  }

  ensure() {
    if (typeof window === 'undefined') return false;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    if (!this.ctx) {
      try {
        this.ctx = new AC();
      } catch (e) {
        return false;
      }
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return true;
  }

  setEnabled(on) {
    this.enabled = on;
    if (!on) this.stopThinking(true);
  }

  startThinking() {
    if (!this.enabled || !this.ensure() || this.hum) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0, t);
    master.gain.linearRampToValueAtTime(0.028, t + 0.7);
    master.connect(ctx.destination);

    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.value = 108;

    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.value = 162;
    const g2 = ctx.createGain();
    g2.gain.value = 0.35;

    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 2.1;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.01;
    lfo.connect(lfoGain);
    lfoGain.connect(master.gain);

    o1.connect(master);
    o2.connect(g2);
    g2.connect(master);

    o1.start(t);
    o2.start(t);
    lfo.start(t);

    this.hum = { master, o1, o2, lfo };
  }

  stopThinking(immediate = false) {
    if (!this.hum || !this.ctx) return;
    const { master, o1, o2, lfo } = this.hum;
    this.hum = null;
    const t = this.ctx.currentTime;
    const fade = immediate ? 0.08 : 0.45;
    try {
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0, t + fade);
      o1.stop(t + fade + 0.1);
      o2.stop(t + fade + 0.1);
      lfo.stop(t + fade + 0.1);
    } catch (e) { /* nodos ya detenidos */ }
  }

  chime() {
    if (!this.enabled || !this.ensure()) return;
    const ctx = this.ctx;
    const notes = [523.25, 659.25]; // C5, E5
    notes.forEach((freq, i) => {
      const t = ctx.currentTime + i * 0.09;
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.045, t + 0.035);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t);
      o.stop(t + 1.2);
    });
  }
}

const orbAudio = new OrbAudio();
export default orbAudio;
